import { useState, useMemo } from "react";
import { useStore } from "../../../store/store";
import { useTranslation } from "react-i18next";
import { generateSignedEndpoint } from "../../../api/generateSignedEndpoint";
import axios from "axios";
import { IPoll, IPollOption, SocketEvents } from "../../hooks/useSocket/types";
import { twMerge } from "tailwind-merge";

interface IProps {
	poll: IPoll;
	liveId: number | undefined;
}

const PollCard = (props: IProps) => {
	const { poll } = props;
	const { t } = useTranslation();
	const socket = useStore((state) => state.socket);
	const updatePollVote = useStore((state) => state.updatePollVote);
	const [votedOptionId, setVotedOptionId] = useState<number | null>(null);
	const [voting, setVoting] = useState(false);

	const totalVotes = useMemo(
		() => poll.options.reduce((sum, opt) => sum + opt.vote_count, 0),
		[poll.options]
	);

	const hasVoted = votedOptionId !== null;

	const handleVote = async (option: IPollOption) => {
		if (hasVoted || voting) return;
		try {
			setVoting(true);
			const endpoint = `/live/poll-options/options/${option.id}/vote`;
			const signed = await generateSignedEndpoint(endpoint);
			await axios.post(signed);

			const newVoteCount = option.vote_count + 1;
			setVotedOptionId(option.id);
			updatePollVote({ pollId: poll.id, optionId: option.id, vote_count: newVoteCount });

			if (socket?.readyState === WebSocket.OPEN) {
				socket.send(
					JSON.stringify({
						type: SocketEvents.pollVote,
						data: { pollId: poll.id, optionId: option.id, vote_count: newVoteCount },
					})
				);
			}
		} catch {
			console.error("Failed to vote");
		} finally {
			setVoting(false);
		}
	};

	return (
		<div className="overflow-hidden rounded-xl border border-primary/15 bg-rte shadow-sm">
			{/* Header */}
			<div className="flex items-start gap-3 border-b border-primary/10 px-4 py-3">
				<div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
					<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
						<path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" />
					</svg>
				</div>
				<div className="flex-1">
					<h3 className="text-sm font-semibold leading-snug text-primary">
						{poll.question}
					</h3>
					{!hasVoted && (
						<p className="mt-1 text-xs text-primary/50">
							{t("live_page.tap_to_vote")}
						</p>
					)}
					{hasVoted && (
						<p className="mt-1 flex items-center gap-1 text-xs text-primary/50">
							<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
							{t("live_page.vote_recorded")}
						</p>
					)}
				</div>
			</div>

			{/* Options */}
			<div className="flex flex-col gap-2 p-4">
				{poll.options.map((option) => {
					const percentage =
						totalVotes > 0 ? Math.round((option.vote_count / totalVotes) * 100) : 0;
					const isSelected = votedOptionId === option.id;

					if (!hasVoted) {
						return (
							<button
								key={option.id}
								onClick={() => handleVote(option)}
								disabled={voting}
								className="group flex w-full items-center gap-3 rounded-lg border border-primary/20 px-4 py-3 text-left transition-all hover:border-primary/40 hover:bg-primary/5 active:scale-[0.98] disabled:opacity-60"
							>
								<span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 border-primary/30 transition-colors group-hover:border-primary/60" />
								<span className="flex-1 text-sm font-medium text-primary">
									{option.poll_option}
								</span>
								{totalVotes > 0 && (
									<span className="text-xs text-primary/40">
										{option.vote_count}
									</span>
								)}
							</button>
						);
					}

					return (
						<div
							key={option.id}
							className={twMerge(
								"relative overflow-hidden rounded-lg border px-4 py-3 transition-all",
								isSelected
									? "border-primary/30 bg-primary/[0.06]"
									: "border-primary/10"
							)}
						>
							<div
								className={twMerge(
									"absolute inset-y-0 left-0 transition-all duration-700 ease-out",
									isSelected ? "bg-primary/10" : "bg-primary/[0.04]"
								)}
								style={{ width: `${percentage}%` }}
							/>
							<div className="relative z-10 flex items-center gap-3">
								{isSelected && (
									<span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-green-500">
										<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
									</span>
								)}
								<span
									className={twMerge(
										"flex-1 text-sm text-primary",
										isSelected && "font-semibold"
									)}
								>
									{option.poll_option}
								</span>
								<span className="whitespace-nowrap text-xs font-medium text-primary/50">
									{percentage}% <span className="text-primary/30">({option.vote_count})</span>
								</span>
							</div>
						</div>
					);
				})}
			</div>

			{/* Footer */}
			<div className="flex items-center justify-between border-t border-primary/5 px-4 py-2.5">
				<span className="text-xs text-primary/40">
					{totalVotes} {t("live_page.total_votes")}
				</span>
				{voting && (
					<span className="flex items-center gap-1.5 text-xs text-primary/50">
						<svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
							<circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
							<path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" />
						</svg>
						{t("live_page.voting")}
					</span>
				)}
			</div>
		</div>
	);
};

export default PollCard;
